import { apiClient } from './apiClient';
import { camelize } from './_caseTransform';
import type { Cloud, CloudRegion } from '../types/cloud';

export const cloudService = {
  /**
   * Get all available cloud providers
   */
  // No mock fallback on clouds/regions: a transient failure silently returning
  // the static Azure-only list poisoned every cloud/region picker (real clouds
  // vanished until reload). Let failures throw so CloudContext can retry.
  async getClouds(): Promise<Cloud[]> {
    const response = camelize(await apiClient.getAll<Cloud>('/clouds/'));
    return response.data;
  },

  /**
   * Get cloud regions, optionally filtered by cloud
   */
  async getCloudRegions(cloudId?: string): Promise<CloudRegion[]> {
    const endpoint = cloudId
      ? `/clouds/${cloudId}/regions`
      : '/cloud-regions/';

    const response = camelize(await apiClient.getAll<CloudRegion>(endpoint));
    return response.data;
  },

  /**
   * Cloud regions an organization can actually deploy into — only those whose cloud
   * has an eligible Kubernetes cluster for the org (honoring its infra flags). Used to
   * keep the wizard region picker from offering regions the org can't deploy to (e.g. a
   * self-managed-only org shouldn't see the system infra's clouds).
   */
  async getDeployableCloudRegions(organizationId: string): Promise<CloudRegion[]> {
    const response = camelize(await apiClient.getAll<CloudRegion>(
      `/cloud-regions/?deployable_for_organization_id=${encodeURIComponent(organizationId)}`,
      [],
    ));
    return response.data;
  },

  /**
   * Get a single cloud region by ID
   */
  async getCloudRegion(regionId: string): Promise<CloudRegion | null> {
    const response = camelize(await apiClient.get<CloudRegion>(
      `/cloud-regions/${regionId}`,
    ));
    return response.data;
  },
};
